import { ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Copy } from "lucide-react";
import { Surface } from "../../../../shared/ui/primitives";
import { PRESENTING_PROBLEMS } from "../../../../shared/domain/presentingProblems";
import { THERAPY_TYPES } from "../../../../shared/domain/therapyTypes";
import {
    usePublicProfileSettings,
    usePublish,
    useUnpublish,
    useUpdateContact,
    useUpdateCopy,
    useUpdateVisibility,
} from "../../application/usePublicProfileSettings";
import {
    Modality,
    PresentingProblem,
    PublicProfileSettings,
    TherapyType,
} from "../../domain/types";
import { DIRECTORY_ENABLED } from "../../domain/availability";
import ProfilePhotoUploader from "./ProfilePhotoUploader";
import TagPicker, { TagOption } from "../../../../shared/ui/form/TagPicker";
import { partitionTags as partition } from "../../../../shared/ui/form/partitionTags";
import VisibilitySwitch from "./VisibilitySwitch";

const MODALITIES: Modality[] = ["online", "inPerson"];
const MAX_PROBLEMS = 8;
const MAX_TYPES = 8;

const inputClasses =
    "w-full rounded-md border border-border-strong bg-surface-raised px-4 py-2.5 text-sm text-content-primary placeholder:text-content-tertiary focus:border-accent focus:outline-2 focus:outline-offset-2 focus:outline-accent-text";

export default function PublicProfileSettingsCard() {
    const { t } = useTranslation();
    const { data: profile, isLoading } = usePublicProfileSettings();

    if (isLoading || !profile) {
        return (
            <Surface as="section" padding="lg">
                <p className="text-sm text-content-tertiary">{t("common.loading")}</p>
            </Surface>
        );
    }

    return <Loaded profile={profile} />;
}

/**
 * One numbered step per surface.
 *
 * The previous single card ran nine unlabelled groups together, so "what am I
 * filling in and why" had to be inferred from field order. Splitting it means
 * each surface answers one question, and the step number gives a therapist who
 * left halfway a place to come back to.
 */
function Step({
    index,
    title,
    description,
    children,
}: {
    index: number;
    title: string;
    description?: string;
    children: ReactNode;
}) {
    return (
        <Surface as="section" padding="lg" className="flex flex-col gap-5">
            <div className="flex items-start gap-3">
                <span
                    aria-hidden="true"
                    className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-accent-tint text-xs font-semibold tabular-nums text-accent-on-tint"
                >
                    {index}
                </span>
                <div className="flex flex-col gap-1">
                    <h3 className="text-base font-semibold text-content-primary">{title}</h3>
                    {description && (
                        <p className="max-w-prose text-sm text-content-secondary">
                            {description}
                        </p>
                    )}
                </div>
            </div>
            {children}
        </Surface>
    );
}

function Field({
    label,
    hint,
    children,
    className = "",
}: {
    label: string;
    hint?: string;
    children: ReactNode;
    className?: string;
}) {
    return (
        <label className={`flex flex-col gap-1.5 ${className}`}>
            <span className="text-sm font-medium text-content-secondary">{label}</span>
            {children}
            {hint && <span className="text-sm text-content-tertiary">{hint}</span>}
        </label>
    );
}

function Loaded({ profile }: { profile: PublicProfileSettings }) {
    const { t } = useTranslation();
    const updateCopy = useUpdateCopy();
    const updateContact = useUpdateContact();
    const updateVisibility = useUpdateVisibility();
    const publish = usePublish();
    const unpublish = useUnpublish();

    const [headline, setHeadline] = useState(profile.headline ?? "");
    const [bio, setBio] = useState(profile.public_bio ?? "");
    const [city, setCity] = useState(profile.city ?? "");
    const [modalities, setModalities] = useState<Modality[]>(profile.modalities);
    const [problems, setProblems] = useState<PresentingProblem[]>(
        profile.presenting_problems,
    );
    const [types, setTypes] = useState<TherapyType[]>(profile.therapy_types);
    const [customProblems, setCustomProblems] = useState<string[]>(
        profile.custom_presenting_problems,
    );
    const [customTypes, setCustomTypes] = useState<string[]>(
        profile.custom_therapy_types,
    );
    const [email, setEmail] = useState(profile.public_email ?? "");
    const [phone, setPhone] = useState(profile.public_phone ?? "");
    const [website, setWebsite] = useState(profile.public_website ?? "");

    // The server owns what is missing for publication, so a save replaces the
    // local draft rather than the other way round.
    useEffect(() => {
        setHeadline(profile.headline ?? "");
        setBio(profile.public_bio ?? "");
        setCity(profile.city ?? "");
        setModalities(profile.modalities);
        setProblems(profile.presenting_problems);
        setTypes(profile.therapy_types);
        setCustomProblems(profile.custom_presenting_problems);
        setCustomTypes(profile.custom_therapy_types);
        setEmail(profile.public_email ?? "");
        setPhone(profile.public_phone ?? "");
        setWebsite(profile.public_website ?? "");
    }, [profile]);

    const problemOptions: TagOption[] = useMemo(
        () =>
            PRESENTING_PROBLEMS.map((problem) => ({
                value: problem,
                label: t(`publicDirectory.problems.${problem}`),
            })),
        [t],
    );

    const typeOptions: TagOption[] = useMemo(
        () =>
            THERAPY_TYPES.map((type) => ({
                value: type,
                label: t(`session.therapyOptions.${type}`),
            })),
        [t],
    );

    // Every copy save sends the whole block, so the field being edited cannot
    // blank a sibling the therapist has not touched this session.
    const saveCopy = (
        overrides: Partial<{
            problems: PresentingProblem[];
            types: TherapyType[];
            customProblems: string[];
            customTypes: string[];
            modalities: Modality[];
        }> = {},
    ) =>
        updateCopy.mutate({
            headline: headline.trim() || null,
            public_bio: bio.trim() || null,
            city: city.trim() || null,
            modalities: overrides.modalities ?? modalities,
            languages: profile.languages,
            presenting_problems: overrides.problems ?? problems,
            therapy_types: overrides.types ?? types,
            custom_presenting_problems: overrides.customProblems ?? customProblems,
            custom_therapy_types: overrides.customTypes ?? customTypes,
        });

    // Sent as a block, because the server validates it as one: a typo in the
    // phone must not leave a half-updated contact block published.
    const saveContact = () =>
        updateContact.mutate({
            public_email: email.trim() || null,
            public_phone: phone.trim() || null,
            public_website: website.trim() || null,
        });

    const setFlag = (flag: keyof PublicProfileSettings, value: boolean) =>
        updateVisibility.mutate({
            show_photo: profile.show_photo,
            show_therapy_types: profile.show_therapy_types,
            show_availability: profile.show_availability,
            show_locations: profile.show_locations,
            show_contact: profile.show_contact,
            [flag]: value,
        });

    // The switch governs both halves, so a therapist whose only declaration is
    // a custom one must still be able to turn it on.
    const declaredTypeLabels = [
        ...profile.therapy_types.map((type) => t(`session.therapyOptions.${type}`)),
        ...profile.custom_therapy_types,
    ];

    const canPublish = profile.missing_for_publication.length === 0;

    return (
        <div className="flex flex-col gap-6">
            <Surface as="section" padding="lg" className="flex flex-col gap-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex flex-col gap-1">
                        <div className="flex flex-wrap items-center gap-2">
                            <h2 className="text-xl font-semibold text-content-primary">
                                {t("publicDirectory.settings.title")}
                            </h2>
                            <span className="rounded-full bg-accent-tint px-2 py-0.5 text-xs font-semibold uppercase tracking-[0.08em] text-accent-on-tint">
                                {t("publicDirectory.settings.beta")}
                            </span>
                        </div>
                        <p className="max-w-prose text-sm text-content-secondary">
                            {t("publicDirectory.settings.subtitle")}
                        </p>
                        {/* Said before the first switch, not after publishing:
                            what a therapist agrees to here is a surface that
                            is still moving. */}
                        <p className="max-w-prose text-sm text-content-tertiary">
                            {t("publicDirectory.settings.betaNote")}
                        </p>
                    </div>

                    {profile.is_published ? (
                        <div className="flex flex-col items-end gap-2">
                            <a
                                href={profile.public_path}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sm font-medium text-accent-text hover:underline"
                            >
                                {t("publicDirectory.settings.viewPublic")}
                            </a>
                            <button
                                type="button"
                                onClick={() => unpublish.mutate()}
                                className="rounded-lg border border-border-strong px-4 py-2.5 text-sm font-medium text-content-primary transition-colors duration-[--duration-fast] hover:bg-surface-sunken focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text"
                            >
                                {t("publicDirectory.settings.unpublish")}
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => publish.mutate()}
                            disabled={
                                !DIRECTORY_ENABLED ||
                                !canPublish ||
                                publish.isPending
                            }
                            title={
                                DIRECTORY_ENABLED
                                    ? undefined
                                    : t("publicDirectory.settings.betaDisabled")
                            }
                            className="rounded-lg bg-accent px-5 py-2.5 text-sm font-semibold text-accent-contrast transition-colors duration-[--duration-fast] hover:bg-accent-hover disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text"
                        >
                            {t("publicDirectory.settings.publish")}
                        </button>
                    )}
                </div>

                <p
                    className={`flex items-center gap-2 text-sm ${
                        profile.is_published
                            ? "text-status-success-on-tint"
                            : "text-content-secondary"
                    }`}
                >
                    <span
                        aria-hidden="true"
                        className={`h-2 w-2 rounded-full ${
                            profile.is_published ? "bg-status-success" : "bg-content-tertiary"
                        }`}
                    />
                    {profile.is_published
                        ? t("publicDirectory.settings.statusPublished")
                        : t("publicDirectory.settings.statusDraft")}
                </p>

                {!canPublish && (
                    <Surface tone="warning" padding="sm" radius="lg">
                        <p className="text-sm text-status-warning-on-tint">
                            {t("publicDirectory.settings.missing", {
                                fields: profile.missing_for_publication
                                    .map((field) =>
                                        t(`publicDirectory.settings.fields.${field}`),
                                    )
                                    .join(", "),
                            })}
                        </p>
                    </Surface>
                )}
            </Surface>

            <Step
                index={1}
                title={t("publicDirectory.settings.steps.presentation")}
                description={t("publicDirectory.settings.steps.presentationHint")}
            >
                <div className="flex flex-col gap-4">
                    <VisibilitySwitch
                        label={t("publicDirectory.settings.switches.photo")}
                        checked={profile.show_photo}
                        onChange={(value) => setFlag("show_photo", value)}
                    />
                    <ProfilePhotoUploader photoUrl={profile.photo_url} />
                </div>

                <Field label={t("publicDirectory.settings.headline")}>
                    <input
                        className={inputClasses}
                        value={headline}
                        maxLength={120}
                        onChange={(e) => setHeadline(e.target.value)}
                        onBlur={() => saveCopy()}
                        placeholder={t("publicDirectory.settings.headlinePlaceholder")}
                    />
                </Field>

                {/* The hint is said plainly, because the public description and
                    the internal one are easy to confuse and the internal one may
                    name patients or rates. */}
                <Field
                    label={t("publicDirectory.settings.bio")}
                    hint={t("publicDirectory.settings.bioSeparateNote")}
                >
                    <textarea
                        className={`${inputClasses} min-h-40`}
                        value={bio}
                        maxLength={3000}
                        onChange={(e) => setBio(e.target.value)}
                        onBlur={() => saveCopy()}
                        placeholder={t("publicDirectory.settings.bioPlaceholder")}
                    />
                </Field>
            </Step>

            <Step
                index={2}
                title={t("publicDirectory.settings.steps.reach")}
                description={t("publicDirectory.settings.steps.reachHint")}
            >
                <Field
                    label={t("publicDirectory.filters.city")}
                    className="sm:max-w-xs"
                >
                    <input
                        className={inputClasses}
                        value={city}
                        maxLength={80}
                        onChange={(e) => setCity(e.target.value)}
                        onBlur={() => saveCopy()}
                    />
                </Field>

                <fieldset className="flex flex-col gap-2">
                    <legend className="mb-2 text-sm font-medium text-content-secondary">
                        {t("publicDirectory.filters.modality")}
                    </legend>
                    <div className="flex gap-2">
                        {MODALITIES.map((modality) => {
                            const on = modalities.includes(modality);
                            return (
                                <button
                                    key={modality}
                                    type="button"
                                    aria-pressed={on}
                                    onClick={() => {
                                        const next = on
                                            ? modalities.filter((m) => m !== modality)
                                            : [...modalities, modality];
                                        setModalities(next);
                                        saveCopy({ modalities: next });
                                    }}
                                    className={`rounded-full border px-4 py-2.5 text-sm transition-colors duration-[--duration-fast] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text ${
                                        on
                                            ? "border-accent/30 bg-accent-tint font-medium text-accent-on-tint"
                                            : "border-border-subtle text-content-secondary hover:border-border-strong hover:bg-surface-sunken"
                                    }`}
                                >
                                    {t(`publicDirectory.modality.${modality}`)}
                                </button>
                            );
                        })}
                    </div>
                </fieldset>

                {/* Shown, not relayed: there is no contact form, so these are the
                    only way someone can reach out. Kept beside the city because
                    both answer "how do I reach this person". */}
                <div className="flex flex-col gap-4 border-t border-border-subtle pt-5">
                    <div className="flex flex-col gap-1">
                        <h4 className="text-sm font-semibold text-content-primary">
                            {t("publicDirectory.blocks.contact")}
                        </h4>
                        <p className="max-w-prose text-sm text-content-secondary">
                            {t("publicDirectory.settings.contactNote")}
                        </p>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-3">
                        <Field label={t("publicDirectory.settings.contactEmail")}>
                            <input
                                type="email"
                                className={inputClasses}
                                value={email}
                                maxLength={254}
                                placeholder="hola@ejemplo.com"
                                onChange={(e) => setEmail(e.target.value)}
                                onBlur={saveContact}
                            />
                        </Field>

                        <Field label={t("publicDirectory.settings.contactPhone")}>
                            <input
                                type="tel"
                                className={inputClasses}
                                value={phone}
                                maxLength={30}
                                placeholder="+34 600 00 00 00"
                                onChange={(e) => setPhone(e.target.value)}
                                onBlur={saveContact}
                            />
                        </Field>

                        <Field label={t("publicDirectory.settings.contactWebsite")}>
                            <input
                                type="url"
                                className={inputClasses}
                                value={website}
                                maxLength={200}
                                placeholder="https://…"
                                onChange={(e) => setWebsite(e.target.value)}
                                onBlur={saveContact}
                            />
                        </Field>
                    </div>

                    {updateContact.isError && (
                        <p role="alert" className="text-sm text-status-critical">
                            {t("publicDirectory.settings.contactRejected")}
                        </p>
                    )}
                </div>
            </Step>

            {/* Before the therapy types, because it is what a visitor searches
                on — they know they cannot sleep, not that they want schema
                therapy. */}
            <Step
                index={3}
                title={t("publicDirectory.settings.problems", { max: MAX_PROBLEMS })}
                description={`${t("publicDirectory.settings.steps.problemsHint")} ${t("publicDirectory.settings.picker.customHint")}`}
            >
                <TagPicker
                    options={problemOptions}
                    selected={[...problems, ...customProblems]}
                    max={MAX_PROBLEMS}
                    allowCustom
                    onChange={(next) => {
                        const { known, custom } = partition(next, PRESENTING_PROBLEMS);
                        setProblems(known as PresentingProblem[]);
                        setCustomProblems(custom);
                        saveCopy({
                            problems: known as PresentingProblem[],
                            customProblems: custom,
                        });
                    }}
                    searchLabel={t("publicDirectory.settings.picker.searchProblems")}
                    inputLabel={t("publicDirectory.settings.picker.searchProblems")}
                    emptyLabel={t("publicDirectory.settings.picker.emptyProblems")}
                />
            </Step>

            {/* How they work, as opposed to what they work with. Declared here
                but stored on the therapist: it also pre-fills the therapy type
                when a session is created, which has to work for someone who
                never publishes anything. */}
            <Step
                index={4}
                title={t("publicDirectory.settings.therapyTypes", { max: MAX_TYPES })}
                description={`${t("publicDirectory.settings.steps.typesHint")} ${t("publicDirectory.settings.picker.customHint")}`}
            >
                <TagPicker
                    options={typeOptions}
                    selected={[...types, ...customTypes]}
                    max={MAX_TYPES}
                    allowCustom
                    onChange={(next) => {
                        const { known, custom } = partition(next, THERAPY_TYPES);
                        setTypes(known as TherapyType[]);
                        setCustomTypes(custom);
                        saveCopy({
                            types: known as TherapyType[],
                            customTypes: custom,
                        });
                    }}
                    searchLabel={t("publicDirectory.settings.picker.searchTypes")}
                    inputLabel={t("publicDirectory.settings.picker.searchTypes")}
                    emptyLabel={t("publicDirectory.settings.picker.emptyTypes")}
                />
            </Step>

            <Step
                index={5}
                title={t("publicDirectory.settings.whatIsPublished")}
                description={t("publicDirectory.settings.steps.visibilityHint")}
            >
                <div className="flex flex-col gap-4">
                    <VisibilitySwitch
                        label={t("publicDirectory.settings.switches.therapyTypes")}
                        hint={
                            declaredTypeLabels.length
                                ? declaredTypeLabels.join(" · ")
                                : t("publicDirectory.settings.switches.noTherapyTypes")
                        }
                        checked={profile.show_therapy_types}
                        onChange={(value) => setFlag("show_therapy_types", value)}
                        disabled={declaredTypeLabels.length === 0}
                    />

                    <VisibilitySwitch
                        label={t("publicDirectory.settings.switches.availability")}
                        hint={t("publicDirectory.settings.switches.availabilityHint")}
                        checked={profile.show_availability}
                        onChange={(value) => setFlag("show_availability", value)}
                    />

                    <VisibilitySwitch
                        label={t("publicDirectory.settings.switches.locations")}
                        hint={t("publicDirectory.settings.switches.locationsHint")}
                        checked={profile.show_locations}
                        onChange={(value) => setFlag("show_locations", value)}
                    />

                    <VisibilitySwitch
                        label={t("publicDirectory.settings.switches.contact")}
                        checked={profile.show_contact}
                        onChange={(value) => setFlag("show_contact", value)}
                    />
                </div>

                {/* What a stranger receives, assembled from the same flags the
                    switches above set. Shown here so the therapist sees the shape
                    of their profile before it is live, not after. */}
                <div className="flex flex-col gap-3 border-t border-border-subtle pt-5">
                    <h4 className="text-sm font-semibold text-content-primary">
                        {t("publicDirectory.settings.preview")}
                    </h4>
                    <ul className="flex flex-col gap-1.5 text-sm">
                        {[
                            {
                                label: t("publicDirectory.blocks.about"),
                                on: Boolean(profile.public_bio),
                            },
                            {
                                label: t("publicDirectory.settings.switches.photo"),
                                on: profile.show_photo && Boolean(profile.photo_url),
                            },
                            {
                                label: t("publicDirectory.blocks.worksWith"),
                                on: problems.length + customProblems.length > 0,
                            },
                            {
                                label: t("publicDirectory.blocks.therapyTypes"),
                                on:
                                    profile.show_therapy_types &&
                                    profile.therapy_types.length +
                                        profile.custom_therapy_types.length >
                                        0,
                            },
                            {
                                label: t("publicDirectory.blocks.hours"),
                                on: profile.show_availability,
                            },
                            {
                                label: t("publicDirectory.blocks.locations"),
                                on: profile.show_locations,
                            },
                            {
                                label: t("publicDirectory.blocks.contact"),
                                on:
                                    profile.show_contact &&
                                    Boolean(
                                        profile.public_email ||
                                            profile.public_phone ||
                                            profile.public_website,
                                    ),
                            },
                        ].map(({ label, on }) => (
                            <li key={label} className="flex items-center gap-2">
                                <span
                                    aria-hidden="true"
                                    className={`h-1.5 w-1.5 rounded-full ${on ? "bg-accent" : "bg-content-tertiary"}`}
                                />
                                <span
                                    className={
                                        on ? "text-content-primary" : "text-content-tertiary"
                                    }
                                >
                                    {label}
                                </span>
                                <span className="text-sm text-content-tertiary">
                                    {on
                                        ? t("publicDirectory.settings.previewShown")
                                        : t("publicDirectory.settings.previewHidden")}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </Step>

            <Step
                index={6}
                title={t("publicDirectory.settings.address")}
                description={t("publicDirectory.settings.addressNote")}
            >
                <ProfileLink path={profile.public_path} />
            </Step>
        </div>
    );
}

/**
 * The address is issued from the therapist's name and never changes, so this
 * step hands the link over instead of asking for one: read it, copy it, paste
 * it wherever the therapist wants to be found.
 */
function ProfileLink({ path }: { path: string }) {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    const url = `${window.location.origin}${path}`;

    // The confirmation is transient: it clears itself rather than claiming a
    // clipboard that has long since moved on still holds the link.
    useEffect(() => {
        if (!copied) return;
        const timer = window.setTimeout(() => setCopied(false), 2000);
        return () => window.clearTimeout(timer);
    }, [copied]);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
        } catch {
            setCopied(false);
        }
    };

    return (
        <div className="flex flex-wrap items-center gap-3">
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="break-all rounded-md bg-surface-sunken px-3 py-2 text-sm text-content-primary underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text"
            >
                {url}
            </a>
            <button
                type="button"
                onClick={copy}
                className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-border-strong px-4 py-2 text-sm font-medium text-content-primary transition-colors duration-[--duration-fast] hover:bg-surface-sunken focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text"
            >
                {copied ? (
                    <Check aria-hidden="true" className="h-4 w-4" />
                ) : (
                    <Copy aria-hidden="true" className="h-4 w-4" />
                )}
                {t("publicDirectory.settings.copyLink")}
            </button>
            <span role="status" aria-live="polite" className="text-sm text-content-tertiary">
                {copied ? t("publicDirectory.settings.linkCopied") : ""}
            </span>
        </div>
    );
}
